import { Component, OnInit, AfterViewInit } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

declare const lucide: any;

@Component({
  selector: 'app-drona',
  templateUrl: './drona.component.html',
  styleUrls: ['./drona.component.css'],
  animations: [
    trigger('fadeInUp', [
      state('in', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate('0.6s ease-in-out')
      ])
    ]),
    trigger('slideInLeft', [
      state('in', style({ opacity: 1, transform: 'translateX(0)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'translateX(-30px)' }),
        animate('0.6s ease-in-out')
      ])
    ]),
    trigger('slideInRight', [
      state('in', style({ opacity: 1, transform: 'translateX(0)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'translateX(30px)' }),
        animate('0.6s ease-in-out')
      ])
    ])
  ]
})
export class DronaComponent implements OnInit, AfterViewInit {

  // Banner content
  bannerContent = {
    title: "DRONA — Digital Asset Rights Management",
    subtitle: "A next-generation platform to protect, control, and govern enterprise data with encryption, access management, and compliance.",
    description: "Every file your enterprise creates—from confidential proposals and financial reports to proprietary CAD designs and source code—is a high-value digital asset. Without a robust control system, these assets are vulnerable to misuse, leaks, or theft. DRONA transforms your data security strategy with the command-and-control system to safeguard sensitive information across its entire lifecycle."
  };

  // Key Features - Unified Protection
  keyFeatures = [
    {
      icon: 'shield',
      title: 'Unified Protection for Every Digital Asset',
      description: 'Every file your enterprise creates—from confidential proposals and financial reports to proprietary CAD designs and source code—is a high-value digital asset. DRONA provides the command-and-control system to safeguard sensitive information across its entire lifecycle with encryption, precise access management, and continuous monitoring.'
    },
    {
      icon: 'globe',
      title: 'Access Anywhere, Manage Everything',
      description: 'DRONA empowers you to set the rules for your data and enforce them everywhere. Define exactly who can interact with a file with granular access control based on user role, location, or device.'
    },
    {
      icon: 'activity',
      title: 'Every Click Matters – And We Log Them All',
      description: 'Gain comprehensive, real-time understanding of how your critical data is being used across your entire network with complete audit trails and AI-driven security monitoring.'
    },
    {
      icon: 'lock',
      title: 'Security Without Compromise',
      description: 'Built on Zero-Trust architecture with enterprise-grade AES-256 encryption, built-in threat defense, and compliance with global mandates including DPDP, GDPR, and HIPAA.'
    }
  ];

  // Capabilities - Access Control Features
  capabilities = [
    {
      icon: 'users',
      title: 'Granular Access Control',
      description: 'Define exactly who can interact with a file. Grant permissions for viewing, editing, printing, copying, or forwarding based on user role (RBAC), location, or device.'
    },
    {
      icon: 'refresh-cw',
      title: 'Instant Revocation',
      description: 'Data leaves your network, but your control doesn\'t. Instantly modify or revoke access to any file, anytime, even after it has been shared externally.'
    },
    {
      icon: 'shield-check',
      title: 'Automated Deterrence',
      description: 'Automatically apply dynamic watermarking to documents and enforce download restrictions and screen-capture protection to prevent unauthorized data loss.'
    },
    {
      icon: 'file-text',
      title: 'Audit Trails & Reports',
      description: 'Maintain a complete, immutable record of every file interaction to support compliance, governance, and security reviews.'
    }
  ];

  // Additional Enterprise Management Features
  additionalFeatures = [
    {
      icon: 'layers',
      title: 'Centralized Dashboard',
      description: 'Manage all access policies, monitor user activity, and view security analytics from a unified command center.'
    },
    {
      icon: 'key',
      title: 'SSO & Identity Integration',
      description: 'Seamlessly integrate with Active Directory, LDAP, and modern identity providers for streamlined authentication.'
    },
    {
      icon: 'bell',
      title: 'Real-Time Alerts',
      description: 'Get instant notifications for suspicious activities, policy violations, or unauthorized access attempts.'
    },
    {
      icon: 'bar-chart',
      title: 'Usage Analytics',
      description: 'Comprehensive insights into file access patterns, user behavior, and data usage across your organization.'
    },
    {
      icon: 'settings',
      title: 'Policy Templates',
      description: 'Pre-configured security templates for common use cases to accelerate deployment and ensure consistency.'
    },
    {
      icon: 'cloud',
      title: 'Cloud & On-Premise',
      description: 'Deploy DRONA on your infrastructure or use our cloud-hosted solution—your choice, same powerful control.'
    }
  ];

  // Benefits - DRONA Advantage
  benefits = [
    {
      icon: 'shield-check',
      title: 'Regulatory Alignment',
      category: 'Compliance',
      description: 'DRONA automates compliance reporting and provides comprehensive audit trails that meet the strictest regulatory requirements. Our platform continuously monitors and enforces policies across your entire digital asset ecosystem, ensuring you remain compliant with DPDP, GDPR, HIPAA, and other global mandates without manual overhead.',
      image: 'assets/new/standard-quality-control-concept-m.jpg',
      features: [
        'Automated compliance reporting',
        'DPDP, GDPR, HIPAA ready',
        'Comprehensive audit trails',
        'Real-time policy enforcement'
      ]
    },
    {
      icon: 'code',
      title: 'Open-Source Flexibility',
      category: 'Innovation',
      description: 'Built on open-source foundations, DRONA gives you complete control over your digital asset management strategy. Customize, extend, and scale without restrictions. No hidden costs, no proprietary constraints—just powerful, flexible protection that grows with your business.',
      image: 'assets/new/saas-concept-collage.jpg',
      features: [
        'No vendor lock-in',
        'Full source code access',
        'Customizable architecture',
        'Community-driven innovation'
      ]
    },
    {
      icon: 'shield',
      title: 'Defense-Grade Security',
      category: 'Security',
      description: 'DRONA is engineered to meet the rigorous security standards of defense and intelligence organizations. With AES-256 encryption, zero-trust architecture, and advanced threat detection, your sensitive data receives the highest level of protection available in the industry.',
      image: 'assets/new/4560.jpg',
      features: [
        'AES-256 encryption',
        'Zero-trust architecture',
        'Advanced threat detection',
        'Military-grade standards'
      ]
    },
    {
      icon: 'plug',
      title: 'Seamless Integration',
      category: 'Integration',
      description: 'DRONA integrates seamlessly with your existing technology stack. Our comprehensive API suite and pre-built connectors enable rapid deployment with your ERP systems, collaboration tools, cloud storage, and business applications—no disruption to your workflows.',
      image: 'assets/new/integration.jpg',
      features: [
        'RESTful APIs',
        'Pre-built connectors',
        'Plug-and-play integration',
        'Enterprise ecosystem ready'
      ]
    }
  ];

  // Use Cases - Enterprise Applications
  useCases = [
    {
      title: 'Confidential Documents',
      description: 'Protect sensitive proposals, financial reports, and strategic documents with granular access control and instant revocation capabilities.',
      image: 'assets/newimages/logistics-means-transport-together-with-technological-futuristic-hologram.jpg'
    },
    {
      title: 'Proprietary Designs',
      description: 'Secure CAD designs, source code, and intellectual property with dynamic watermarking and screen-capture protection.',
      image: 'assets/newimages/15727.jpg'
    },
    {
      title: 'Compliance Management',
      description: 'Ensure adherence to DPDP, GDPR, and HIPAA with comprehensive audit trails and automated compliance reporting.',
      image: 'assets/newimages/soldier-using-virtual-tablet-hologram-army-technology.jpg'
    },
    {
      title: 'Enterprise Integration',
      description: 'Seamlessly integrate with existing ERP and workflow systems through ready-made APIs for unified digital asset management.',
      image: 'assets/newimages/view-male-engineer-work-engineers-day-celebration.jpg'
    }
  ];

  // Security Architecture Components
  securityLayers = [
    {
      layer: 'Layer 1',
      title: 'Perimeter Defense',
      icon: 'shield',
      description: 'Multi-layered perimeter protection with advanced firewalls, intrusion detection systems, and network segmentation.',
      features: [
        'Next-generation firewall protection',
        'Real-time threat intelligence',
        'Automated threat blocking',
        'Network segmentation and isolation'
      ],
      image: 'assets/new/cybersecurity-concept-collage-design.jpg'
    },
    {
      layer: 'Layer 2',
      title: 'Data Encryption',
      icon: 'lock',
      description: 'AES-256 encryption protects your data at rest, in transit, and in use with granular encryption controls and key management.',
      features: [
        'AES-256 end-to-end encryption',
        'Zero-knowledge architecture',
        'Hardware security modules',
        'Dynamic key rotation'
      ],
      image: 'assets/new/6537848.jpg'
    },
    {
      layer: 'Layer 3',
      title: 'Identity & Access',
      icon: 'key',
      description: 'Zero-trust authentication with multi-factor authentication, biometric verification, and role-based access controls.',
      features: [
        'Multi-factor authentication',
        'Single sign-on (SSO)',
        'Biometric verification',
        'Privileged access management'
      ],
      image: 'assets/new/identity.jpg'
    },
    {
      layer: 'Layer 4',
      title: 'Behavioral Analytics',
      icon: 'activity',
      description: 'AI-powered behavioral analytics detect anomalies, predict threats, and automate incident response in real-time.',
      features: [
        'AI-driven threat detection',
        'User behavior analytics',
        'Anomaly detection algorithms',
        'Automated incident response'
      ],
      image: 'assets/new/analytics-comparison-information-networking-concept.jpg'
    },
    {
      layer: 'Layer 5',
      title: 'Compliance & Auditing',
      icon: 'file-check',
      description: 'Comprehensive compliance frameworks and immutable audit trails ensure adherence to global regulatory standards.',
      features: [
        'Automated compliance reporting',
        'Immutable audit logs',
        'GDPR, HIPAA, DPDP ready',
        'Real-time compliance monitoring'
      ],
      image: 'assets/new/standard-quality-control-collage-concept.jpg'
    },
    {
      layer: 'Layer 6',
      title: 'Backup & Recovery',
      icon: 'database',
      description: 'Enterprise-grade backup and disaster recovery ensure business continuity with rapid data restoration capabilities.',
      features: [
        'Automated daily backups',
        'Point-in-time recovery',
        'Geo-redundant storage',
        'RTO under 1 hour'
      ],
      image: 'assets/new/drawings-connected-devices.jpg'
    }
  ];

  // Security Standards & Certifications
  securityStandards = [
    {
      name: 'ISO 27001',
      description: 'Information Security Management',
      icon: 'award'
    },
    {
      name: 'SOC 2 Type II',
      description: 'Security, Availability & Confidentiality',
      icon: 'shield-check'
    },
    {
      name: 'GDPR',
      description: 'European Data Protection',
      icon: 'globe'
    },
    {
      name: 'HIPAA',
      description: 'Healthcare Data Protection',
      icon: 'heart'
    },
    {
      name: 'DPDP',
      description: 'Digital Personal Data Protection',
      icon: 'file-shield'
    },
    {
      name: 'Zero Trust',
      description: 'Never Trust, Always Verify',
      icon: 'lock'
    }
  ];

  // Security Statistics
  securityStats = [
    {
      number: '99.99%',
      label: 'Uptime SLA',
      icon: 'trending-up'
    },
    {
      number: '256-bit',
      label: 'AES Encryption',
      icon: 'lock'
    },
    {
      number: '< 1ms',
      label: 'Threat Detection',
      icon: 'zap'
    },
    {
      number: 'Zero',
      label: 'Known Breaches',
      icon: 'shield-check'
    }
  ];

  constructor() { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.initializeAnimations();
    this.initializeLucideIcons();
  }

  private initializeAnimations(): void {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          // Reinitialize icons when elements come into view
          this.initializeLucideIcons();
        }
      });
    }, observerOptions);

    // Observe all elements with data-animate attribute
    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });
  }

  private initializeLucideIcons(): void {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }, 100);
  }
}
